import { Group } from '@visx/group';
import { Sankey as VisxSankey, sankeyJustify } from '@visx/sankey';

import { Bit } from '@blockworks/ui/bit';
import { useMergeRefs } from '@blockworks/ui/hooks';
import { Text } from '@blockworks/ui/text';

import { formatValue } from '../../../../format/utils';
import { useResponsiveChart, useVisualizationTooltip } from '../../../utils';
import { TooltipContainer } from '../tooltip';

import { SankeyConnections } from './connections';
import type { SankeyLinkDatum, SankeyNodeDatum, SankeyProps, SankeyTooltipData } from './sankey.model';
import { useSankey } from './utils';

const MARGIN = 10;
const margin = { y: MARGIN * 2, x: MARGIN * 2 };

const Sankey = ({ activeControls, options, params, data: rawData }: SankeyProps) => {
    const { containerRef: responsiveRef, width, height } = useResponsiveChart();
    const { rootData, nodeWidth, nodePadding, showSourceLabels, showTargetLabels, colorStrategy } = useSankey({
        activeControls,
        options,
        params,
        data: rawData,
    });
    const {
        containerRef,
        TooltipInPortal,
        tooltipData,
        tooltipOpen,
        tooltipLeft,
        tooltipTop,
        showTooltip,
        hideTooltip,
    } = useVisualizationTooltip<SankeyTooltipData>({ useLocalPoint: true });
    const xMax = width - margin.x;
    const yMax = height - margin.y;
    const yMaxPadded = yMax - margin.y;

    const mergeRef = useMergeRefs(responsiveRef, containerRef);

    if (!rootData) {
        console.error('Data error. Sankey graph is falsy.', { rootData });
        return null;
    }

    if (rootData.nodes.length === 0 || rootData.links.length === 0) {
        console.warn('Sankey graph is empty. Add links and nodes to the graph to render the sankey chart.', {
            rootData,
        });
        return null;
    }

    return (
        <Bit.div ref={mergeRef} w="full" h="full" display="flex" pos="relative" overflow="visible">
            <Bit.div pos="relative" borderRadius="sm" style={{ padding: MARGIN }}>
                <svg width={xMax} height={yMax} alignmentBaseline="middle">
                    <Group transform={`translate(0,${MARGIN})`}>
                        <VisxSankey<SankeyNodeDatum, SankeyLinkDatum>
                            root={rootData}
                            nodeWidth={nodeWidth}
                            size={[xMax, yMaxPadded]}
                            nodePadding={nodePadding}
                            nodeAlign={sankeyJustify}
                        >
                            {({ graph, createPath }) => (
                                <SankeyConnections
                                    nodeWidth={nodeWidth}
                                    showTooltip={showTooltip}
                                    hideTooltip={hideTooltip}
                                    graph={graph}
                                    createPath={createPath}
                                    showSourceLabels={showSourceLabels}
                                    showTargetLabels={showTargetLabels}
                                    colorStrategy={colorStrategy}
                                />
                            )}
                        </VisxSankey>
                    </Group>
                </svg>

                {tooltipData && tooltipOpen && (
                    <TooltipInPortal
                        key={`${tooltipLeft}-${tooltipTop}`}
                        applyPositionStyle
                        unstyled
                        left={tooltipLeft}
                        top={tooltipTop}
                        style={useVisualizationTooltip.DEFAULT_STYLES}
                    >
                        <TooltipContainer className="w-auto">
                            {tooltipData.name ? (
                                <Text size="xs" weight="semibold">
                                    {tooltipData.name}
                                </Text>
                            ) : (
                                <Text size="xs" weight="semibold">
                                    {tooltipData.source}
                                    {' → '}
                                    {tooltipData.target}
                                    {' = '}
                                    <Text as="span" weight="regular">
                                        {formatValue(tooltipData.value, tooltipData.valueFormat, 'number')}
                                    </Text>
                                </Text>
                            )}
                        </TooltipContainer>
                    </TooltipInPortal>
                )}
            </Bit.div>
        </Bit.div>
    );
};

export { Sankey };
